'use client'

import { useState, useEffect, useCallback } from 'react'

const STORAGE_KEY = 'pm_fp'
const USAGE_PREFIX = 'pm_usage_'

/**
 * Generate a simple browser fingerprint.
 * Not crypto-grade (intentional) - this is a nudge, not DRM.
 */
function generateFingerprint(): string {
  const signals = [
    navigator.userAgent,
    screen.width.toString(),
    screen.height.toString(),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.language,
    new Date().getTimezoneOffset().toString(),
  ].join('|')

  // Simple hash (djb2)
  let hash = 5381
  for (let i = 0; i < signals.length; i++) {
    hash = (hash * 33) ^ signals.charCodeAt(i)
  }
  const hex = (hash >>> 0).toString(16).padStart(8, '0')
  return `fp_${hex}_${Date.now().toString(36)}`
}

function getFingerprint(): string {
  try {
    let fp = localStorage.getItem(STORAGE_KEY)
    if (!fp) {
      fp = generateFingerprint()
      localStorage.setItem(STORAGE_KEY, fp)
    }
    return fp
  } catch {
    // Incognito or storage disabled
    return generateFingerprint()
  }
}

function getPlatformToken(): string | null {
  try {
    return localStorage.getItem('pm_token')
  } catch {
    return null
  }
}

/**
 * Get local usage count for a tool (optimistic, synced with server).
 */
function getLocalUsage(toolName: string): number {
  try {
    const val = localStorage.getItem(`${USAGE_PREFIX}${toolName}`)
    return val ? parseInt(val, 10) : 0
  } catch {
    return 0
  }
}

function setLocalUsage(toolName: string, count: number): void {
  try {
    localStorage.setItem(`${USAGE_PREFIX}${toolName}`, count.toString())
  } catch {
    // Ignore storage errors
  }
}

export interface ToolUsageState {
  fingerprint: string
  used: number
  remaining: number
  limit: number
  isGated: boolean
  isTokenHolder: boolean
}

/**
 * Hook for tool usage tracking + freemium gating.
 *
 * Returns:
 * - fingerprint: to send as x-tool-fingerprint header
 * - usage state (used, remaining, isGated)
 * - fetchWithUsage: wrapper around fetch that adds headers + handles 403
 */
export function useToolUsage(toolName: string) {
  const isDev = process.env.NODE_ENV === 'development'
  const FREE_LIMIT = isDev ? 999 : 3

  const [state, setState] = useState<ToolUsageState>({
    fingerprint: '',
    used: 0,
    remaining: FREE_LIMIT,
    limit: FREE_LIMIT,
    isGated: false,
    isTokenHolder: isDev,
  })

  useEffect(() => {
    const fp = getFingerprint()
    const token = getPlatformToken()
    const used = isDev ? 0 : getLocalUsage(toolName)

    setState({
      fingerprint: fp,
      used,
      remaining: Math.max(0, FREE_LIMIT - used),
      limit: FREE_LIMIT,
      isGated: isDev ? false : !token && used >= FREE_LIMIT,
      isTokenHolder: isDev || !!token,
    })
  }, [toolName, isDev, FREE_LIMIT])

  /**
   * Wraps a fetch call to add fingerprint + token headers.
   * Handles 403 usage_limit responses by updating gate state.
   * Returns the response (caller handles success/error as before).
   */
  const fetchWithUsage = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const fp = getFingerprint()
      const token = getPlatformToken()

      const headers = new Headers(options.headers)
      headers.set('x-tool-fingerprint', fp)
      if (token) {
        headers.set('x-platform-token', token)
      }

      const response = await fetch(url, { ...options, headers })

      if (response.status === 403) {
        const data = await response.clone().json()
        if (data.error === 'usage_limit') {
          // Sync server state to local
          const serverUsed = data.used ?? FREE_LIMIT
          setLocalUsage(toolName, serverUsed)
          setState(prev => ({
            ...prev,
            used: serverUsed,
            remaining: 0,
            isGated: true,
          }))
          return response
        }
      }

      // On success, update local usage count from server headers
      if (response.ok) {
        const remaining = response.headers.get('x-usage-remaining')
        const used = response.headers.get('x-usage-used')
        if (remaining !== null && used !== null) {
          const usedNum = parseInt(used, 10)
          const remainingNum = parseInt(remaining, 10)
          setLocalUsage(toolName, usedNum)
          setState(prev => ({
            ...prev,
            used: usedNum,
            remaining: remainingNum,
            isGated: false,
          }))
        }
      }

      return response
    },
    [toolName]
  )

  return { ...state, fetchWithUsage }
}
